import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, Navigate, useLocation, useParams } from 'react-router-dom';
import { ArrowUpRight, ChevronDown, Download, FileSpreadsheet, Printer } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { PageHeader } from '@/components/app/PageHeader';
import { EmptyState, Panel, PanelHeader, SkeletonTable } from '@/components/app/States';
import { Note } from '@/components/app/Badges';
import { EditedMark, ExampleDataNote, LinkStat, MiniStat, OperationalPill, SelectField, ServiceStatusPill, TimeStatusPill, useDriftRefs } from '@/components/app/DriftUI';
import { DriftFilterBar, useDriftFilters } from '@/components/app/DriftFilters';
import { CaseBadges, CaseSheetHost, useCaseParam } from '@/components/app/Maskinsaker';
import { DeviationBadges, DeviationKindMark, isClarified } from '@/components/app/Avvikssaker';
import { DRIFT_REPORTS, EVENT_TYPE_LABELS, NOT_CLARIFIED, SERVICE_STATUS_LABELS, TIME_STATUS_LABELS, byUnitText, deviationPath, exportUrl, getDriftReport, hoursText, listMachines, serviceLine } from '@/lib/driftApi';
import { formatDateShort, formatDateTime } from '@/domain/time';
import { fmtNumber } from '@/domain/numbers';
import { cn } from '@/lib/utils';

/*
  Driftsrapport (timer / prosjektforbruk / vareforbruk / maskiner).
  Samme filtre og samme registreringer som sidene Timer, Vareforbruk og Maskinpark. Periode + prosjekt er primære
  filtre, resten ligger under «Flere filtre». Eksport (CSV / XLSX) og utskrift gir alltid det filtrerte utvalget.
  Alle tall lenker til registreringene bak. Ingen økonomi – kun tid og mengder.
*/

const CONFIG = {
  timer: { keys: ['projectId', 'subprojectId', 'employeeId', 'machineId', 'status', 'activityId', 'wageTypeId'], more: ['subprojectId', 'employeeId', 'machineId', 'status', 'activityId', 'wageTypeId'], defaultGroup: 'prosjekt', detailLabel: 'Registreringer' },
  prosjektforbruk: { keys: ['projectId'], more: [], detailLabel: 'Vareforbruk per prosjekt' },
  vareforbruk: { keys: ['projectId', 'subprojectId', 'employeeId', 'category', 'itemId'], more: ['subprojectId', 'category', 'itemId', 'employeeId'], defaultGroup: 'vare', detailLabel: 'Varelinjer' },
  maskiner: { keys: ['projectId', 'machineId'], more: ['machineId'], detailLabel: 'Maskintimer per føring' },
  hendelser: { keys: ['projectId', 'machineId', 'type', 'eventStatus', 'priority', 'assignedToId', 'open'], more: ['machineId', 'type', 'eventStatus', 'priority', 'assignedToId', 'open'], defaultGroup: 'maskin', detailLabel: 'Saker' },
  avvik: { keys: ['projectId', 'kind', 'devStatus', 'severity', 'responsibleId', 'open'], more: ['kind', 'devStatus', 'severity', 'responsibleId', 'open'], defaultGroup: 'prosjekt', detailLabel: 'Avvik' },
};

const HMS_LIST_KEYS = ['projectId', 'kind', 'devStatus', 'severity', 'responsibleId'];

/**
 * Lenke til HMS/RUH/kvalitet-listen med nøyaktig samme utvalg som rapporten: periode settes eksplisitt
 * (listen har «hele perioden» som standard, rapporten 14 dager), og «Utvalg» oversettes til listens visning.
 */
function hmsListLink(f, extra = {}) {
  const open = f.values.open;
  const visning = extra.visning ?? (open === '1' ? 'apne' : open === '0' ? 'lukket' : 'alle');
  const period = { periode: f.period.preset, from: f.period.preset === 'egen' ? f.period.from : undefined, to: f.period.preset === 'egen' ? f.period.to : undefined };
  return `/avvik${f.carry({ ...period, fane: 'hms', ...extra, visning }, { only: HMS_LIST_KEYS })}`;
}

const num = (v, d = 2) => (v === null || v === undefined ? '–' : fmtNumber(v, d));
const qty = (q, unit) => fmtNumber(q, unit === 'stk' ? 0 : unit === 't' ? 1 : Number.isInteger(q) ? 0 : 1);

function suppText(list) {
  if (!list?.length) return '–';
  return list.map((s) => `${s.label} ${fmtNumber(s.quantity, s.unit === 'stk' ? 0 : 1)} ${s.unit}`).join(' · ');
}

/** Lenke til registreringene bak en gruppe. */
function groupLink(type, groupBy, g, carry) {
  if (type === 'timer') {
    if (groupBy === 'prosjekt') return `/timer${carry({ projectId: g.key })}`;
    if (groupBy === 'underprosjekt') {
      const [projectId, subprojectId] = g.key.split(':');
      return `/timer${carry({ projectId, subprojectId: subprojectId || undefined })}`;
    }
    if (groupBy === 'medarbeider') return `/timer${carry({ employeeId: g.key })}`;
    if (groupBy === 'dag') return `/timer${carry({ periode: 'egen', from: g.key, to: g.key })}`;
    if (groupBy === 'aktivitet') return `/timer${carry({ activityId: g.key })}`;
    if (groupBy === 'maskin') return `/timer${carry({ machineId: g.key || 'uten' })}`;
  }
  if (type === 'vareforbruk') {
    if (groupBy === 'vare') return `/vareforbruk${carry({ itemId: g.key })}`;
    if (groupBy === 'prosjekt') return `/vareforbruk${carry({ projectId: g.key })}`;
    if (groupBy === 'kategori') return `/vareforbruk${carry({ category: g.key })}`;
    if (groupBy === 'dag') return `/vareforbruk${carry({ periode: 'egen', from: g.key, to: g.key })}`;
  }
  return null;
}

function OpenCell({ to, testId }) {
  return (
    <TableCell className="w-[1%] whitespace-nowrap text-right">
      <Link to={to} className="inline-flex h-8 items-center gap-1 rounded-[8px] px-2 text-[13px] font-medium text-[var(--hr-accent-text)] hover:bg-[var(--hr-surface-2)] hover:underline print:hidden" data-testid={testId}>
        Åpne <ArrowUpRight className="h-3.5 w-3.5" aria-hidden="true" />
      </Link>
    </TableCell>
  );
}

/* ---------- Sammendrag per rapporttype ---------- */

/** Statusfordeling som kompakt tekst («12 sendt · 2 returnert · 30 godkjent») – samme tall som statuspillene i detaljlisten. */
function statusBreakdown(byStatus = {}) {
  return ['sendt', 'returnert', 'utkast', 'godkjent']
    .filter((k) => byStatus[k])
    .map((k) => `${byStatus[k]} ${TIME_STATUS_LABELS[k].toLowerCase()}`)
    .join(' · ');
}

function TimerSummary({ r, carry }) {
  const t = r.totals;
  return (
    <Table data-testid="report-summary-table">
      <TableHeader>
        <TableRow>
          <TableHead>{r.groupLabel}</TableHead>
          <TableHead>Detalj</TableHead>
          <TableHead className="text-right">Arbeidstimer (t)</TableHead>
          <TableHead className="text-right">Maskintimer (t)</TableHead>
          <TableHead className="text-right">Føringer</TableHead>
          <TableHead className="text-right">Til godkjenning</TableHead>
          <TableHead>Status</TableHead>
          <TableHead>Tillegg</TableHead>
          <TableHead className="print:hidden" />
        </TableRow>
      </TableHeader>
      <TableBody>
        {r.groups.map((g) => (
          <TableRow key={g.key || 'none'} data-testid={`report-group-${g.key || 'none'}`}>
            <TableCell className="font-medium text-[var(--hr-heading)]">{g.label}</TableCell>
            <TableCell className="text-[var(--hr-muted)]">{g.sublabel ?? ''}</TableCell>
            <TableCell className="hr-num text-right font-semibold">{num(g.hours)}</TableCell>
            <TableCell className="hr-num text-right">{g.machineHours ? num(g.machineHours) : <span className="text-[var(--hr-muted-2)]">–</span>}</TableCell>
            <TableCell className="hr-tabular text-right">{g.count}</TableCell>
            <TableCell className={cn('hr-tabular text-right', g.byStatus.sendt ? 'text-[var(--hr-amber)]' : 'text-[var(--hr-muted-2)]')} data-testid={`report-group-pending-${g.key || 'none'}`}>{g.byStatus.sendt ?? 0}</TableCell>
            <TableCell className="hr-tabular min-w-[150px] text-[13px] leading-5 text-[var(--hr-muted)]" data-testid={`report-group-status-${g.key || 'none'}`}>{statusBreakdown(g.byStatus) || '–'}</TableCell>
            <TableCell className="hr-tabular max-w-[280px]">{suppText(g.supplements)}</TableCell>
            <OpenCell to={groupLink('timer', r.groupBy, g, carry)} testId={`report-open-${g.key || 'none'}`} />
          </TableRow>
        ))}
        <TableRow className="bg-[var(--hr-surface-2)] font-semibold hover:bg-[var(--hr-surface-2)]" data-testid="report-summary-total">
          <TableCell className="text-[var(--hr-heading)]">Sum</TableCell>
          <TableCell className="text-[var(--hr-muted)]">{r.groups.length} {r.groupLabel.toLowerCase()}{r.groups.length === 1 ? '' : 'er'}</TableCell>
          <TableCell className="hr-num text-right">{num(t.hours)}</TableCell>
          <TableCell className="hr-num text-right">{num(t.machineHours)}</TableCell>
          <TableCell className="hr-tabular text-right">{t.count}</TableCell>
          <TableCell className="hr-tabular text-right" data-testid="report-total-pending-cell">{t.byStatus.sendt ?? 0}</TableCell>
          <TableCell className="hr-tabular min-w-[150px] text-[13px] leading-5 text-[var(--hr-muted)]" data-testid="report-total-status-cell">{statusBreakdown(t.byStatus) || '–'}</TableCell>
          <TableCell className="hr-tabular">{suppText(t.supplements)}</TableCell>
          <TableCell className="print:hidden" />
        </TableRow>
      </TableBody>
    </Table>
  );
}

function TimerDetail({ r, carry }) {
  const rows = r.groups.flatMap((g) => g.entries).sort((a, b) => (a.date < b.date ? 1 : a.date > b.date ? -1 : a.fromTime < b.fromTime ? 1 : -1));
  return (
    <Table data-testid="report-detail-table">
      <TableHeader>
        <TableRow>
          <TableHead>Dato</TableHead>
          <TableHead>Medarbeider</TableHead>
          <TableHead>Prosjekt</TableHead>
          <TableHead>Aktivitet</TableHead>
          <TableHead>Tid</TableHead>
          <TableHead className="text-right">Timer (t)</TableHead>
          <TableHead>Tillegg</TableHead>
          <TableHead>Maskin</TableHead>
          <TableHead>Status</TableHead>
          <TableHead className="print:hidden" />
        </TableRow>
      </TableHeader>
      <TableBody>
        {rows.map((e) => (
          <TableRow key={e.id}>
            <TableCell className="hr-tabular whitespace-nowrap">{formatDateShort(e.date)}{e.edited && <div className="mt-0.5"><EditedMark /></div>}</TableCell>
            <TableCell className="font-medium text-[var(--hr-heading)]">{e.employee?.name}</TableCell>
            <TableCell>{e.project?.name}{e.subproject && <div className="text-[13px] text-[var(--hr-muted)]">{e.subproject.name}</div>}</TableCell>
            <TableCell>{e.activity?.label}</TableCell>
            <TableCell className="hr-tabular whitespace-nowrap">{e.fromTime}–{e.toTime}{e.breakMinutes ? <span className="text-[var(--hr-muted)]"> · {e.breakMinutes} min</span> : null}</TableCell>
            <TableCell className="hr-num text-right font-semibold">{num(e.hours)}</TableCell>
            <TableCell className="hr-tabular text-[13px]">{suppText(e.supplements)}</TableCell>
            <TableCell className="whitespace-nowrap">{e.machine ? `${e.machine.internalNo} · ${hoursText(e.machineTotalHours)}` : <span className="text-[var(--hr-muted-2)]">–</span>}</TableCell>
            <TableCell><TimeStatusPill status={e.status} /></TableCell>
            <OpenCell to={`/timer${carry({ rediger: e.id })}`} testId={`report-entry-open-${e.id}`} />
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

function ProsjektforbrukSummary({ r, carry, units }) {
  const t = r.totals;
  return (
    <Table data-testid="report-summary-table">
      <TableHeader>
        <TableRow>
          <TableHead>Prosjekt</TableHead>
          <TableHead>Kunde</TableHead>
          <TableHead className="text-right">Arbeidstimer (t)</TableHead>
          <TableHead className="text-right">Maskintimer (t)</TableHead>
          <TableHead className="text-right">Føringer</TableHead>
          <TableHead>Tillegg</TableHead>
          <TableHead>Vareforbruk (per enhet)</TableHead>
          <TableHead className="print:hidden" />
        </TableRow>
      </TableHeader>
      <TableBody>
        {r.rows.map((p) => (
          <TableRow key={p.key} data-testid={`report-group-${p.key}`}>
            <TableCell className="font-medium text-[var(--hr-heading)]">
              {p.label}
              {p.status === 'avsluttet' && <span className="ml-1.5 rounded-full bg-[var(--hr-inset)] px-1.5 py-px text-[12px] font-medium text-[var(--hr-muted)]">avsluttet</span>}
            </TableCell>
            <TableCell className="text-[var(--hr-muted)]">{p.sublabel}</TableCell>
            <TableCell className="hr-num text-right font-semibold">{num(p.hours)}</TableCell>
            <TableCell className="hr-num text-right">{p.machineHours ? num(p.machineHours) : <span className="text-[var(--hr-muted-2)]">–</span>}</TableCell>
            <TableCell className="hr-tabular text-right">{p.timeCount}{p.pending > 0 && <span className="ml-1 text-[12px] text-[var(--hr-amber)]">({p.pending})</span>}</TableCell>
            <TableCell className="hr-tabular max-w-[240px] text-[13px]">{suppText(p.supplements)}</TableCell>
            <TableCell className="hr-tabular text-[13px]">{byUnitText(p.consumptionByUnit, units) ?? <span className="text-[var(--hr-muted-2)]">–</span>}</TableCell>
            <OpenCell to={`/prosjekter/${p.projectId}${carry({}, { only: [] })}`} testId={`report-open-${p.key}`} />
          </TableRow>
        ))}
        <TableRow className="bg-[var(--hr-surface-2)] font-semibold hover:bg-[var(--hr-surface-2)]" data-testid="report-summary-total">
          <TableCell className="text-[var(--hr-heading)]">Sum</TableCell>
          <TableCell className="text-[var(--hr-muted)]">{t.projects} prosjekter</TableCell>
          <TableCell className="hr-num text-right">{num(t.hours)}</TableCell>
          <TableCell className="hr-num text-right">{num(t.machineHours)}</TableCell>
          <TableCell className="hr-tabular text-right">{t.timeCount}</TableCell>
          <TableCell className="hr-tabular text-[13px]">{suppText(t.supplements)}</TableCell>
          <TableCell className="hr-tabular text-[13px]">{byUnitText(t.consumptionByUnit, units) ?? '–'}</TableCell>
          <TableCell className="print:hidden" />
        </TableRow>
      </TableBody>
    </Table>
  );
}

function ProsjektforbrukDetail({ r, carry, units, categories }) {
  const rows = r.rows.flatMap((p) => p.items.map((it) => ({ ...it, project: p.label, projectId: p.projectId })));
  if (!rows.length) return <p className="px-5 py-4 text-[14px] text-[var(--hr-muted)]">Ingen vareforbruk i utvalget.</p>;
  return (
    <Table data-testid="report-detail-table">
      <TableHeader>
        <TableRow>
          <TableHead>Prosjekt</TableHead>
          <TableHead>Kategori</TableHead>
          <TableHead>Vare</TableHead>
          <TableHead className="text-right">Mengde</TableHead>
          <TableHead>Enhet</TableHead>
          <TableHead className="text-right">Linjer</TableHead>
          <TableHead className="print:hidden" />
        </TableRow>
      </TableHeader>
      <TableBody>
        {rows.map((it) => (
          <TableRow key={`${it.projectId}-${it.itemId}`}>
            <TableCell className="font-medium text-[var(--hr-heading)]">{it.project}</TableCell>
            <TableCell>{categories?.find((c) => c.id === it.category)?.label ?? it.category}</TableCell>
            <TableCell>{it.name}</TableCell>
            <TableCell className="hr-num text-right font-semibold">{qty(it.quantity, it.unit)}</TableCell>
            <TableCell>{units?.[it.unit] ?? it.unit}</TableCell>
            <TableCell className="hr-tabular text-right">{it.count}</TableCell>
            <OpenCell to={`/vareforbruk${carry({ projectId: it.projectId, itemId: it.itemId })}`} testId={`report-entry-open-${it.projectId}-${it.itemId}`} />
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

function VareforbrukSummary({ r, carry, units }) {
  const t = r.totals;
  return (
    <Table data-testid="report-summary-table">
      <TableHeader>
        <TableRow>
          <TableHead>{r.groupLabel}</TableHead>
          <TableHead>Detalj</TableHead>
          <TableHead className="text-right">Mengde</TableHead>
          <TableHead>Enhet</TableHead>
          <TableHead>Per enhet</TableHead>
          <TableHead className="text-right">Varelinjer</TableHead>
          <TableHead className="print:hidden" />
        </TableRow>
      </TableHeader>
      <TableBody>
        {r.groups.map((g) => (
          <TableRow key={g.key || 'none'} data-testid={`report-group-${g.key || 'none'}`}>
            <TableCell className="font-medium text-[var(--hr-heading)]">{g.label}</TableCell>
            <TableCell className="text-[var(--hr-muted)]">{g.sublabel ?? ''}</TableCell>
            <TableCell className="hr-num text-right font-semibold">{g.unit ? qty(g.quantity, g.unit) : <span className="text-[var(--hr-muted-2)]" title="Blandede enheter – se «Per enhet»">–</span>}</TableCell>
            <TableCell>{g.unit ? units?.[g.unit] ?? g.unit : <span className="text-[var(--hr-muted)]">blandet</span>}</TableCell>
            <TableCell className="hr-tabular text-[13px]">{byUnitText(g.byUnit, units)}</TableCell>
            <TableCell className="hr-tabular text-right">{g.count}</TableCell>
            <OpenCell to={groupLink('vareforbruk', r.groupBy, g, carry)} testId={`report-open-${g.key || 'none'}`} />
          </TableRow>
        ))}
        <TableRow className="bg-[var(--hr-surface-2)] font-semibold hover:bg-[var(--hr-surface-2)]" data-testid="report-summary-total">
          <TableCell className="text-[var(--hr-heading)]">Sum</TableCell>
          <TableCell className="text-[var(--hr-muted)]">{t.items} varer · {t.projects} prosjekter</TableCell>
          <TableCell className="hr-num text-right text-[var(--hr-muted-2)]">–</TableCell>
          <TableCell className="text-[var(--hr-muted)]">per enhet</TableCell>
          <TableCell className="hr-tabular text-[13px]">{byUnitText(t.byUnit, units) ?? '–'}</TableCell>
          <TableCell className="hr-tabular text-right">{t.lineCount}</TableCell>
          <TableCell className="print:hidden" />
        </TableRow>
      </TableBody>
    </Table>
  );
}

function VareforbrukDetail({ r, carry, units, categories }) {
  const rows = r.groups.flatMap((g) => g.lines).sort((a, b) => (a.date < b.date ? 1 : -1));
  return (
    <Table data-testid="report-detail-table">
      <TableHeader>
        <TableRow>
          <TableHead>Dato</TableHead>
          <TableHead>Prosjekt</TableHead>
          <TableHead>Kategori</TableHead>
          <TableHead>Vare</TableHead>
          <TableHead className="text-right">Mengde</TableHead>
          <TableHead>Enhet</TableHead>
          <TableHead>Registrert av</TableHead>
          <TableHead className="print:hidden" />
        </TableRow>
      </TableHeader>
      <TableBody>
        {rows.map((ln, i) => (
          <TableRow key={`${ln.entryId}-${ln.itemId}-${i}`}>
            <TableCell className="hr-tabular whitespace-nowrap">{formatDateShort(ln.date)}{ln.edited && <div className="mt-0.5"><EditedMark /></div>}</TableCell>
            <TableCell className="font-medium text-[var(--hr-heading)]">{ln.project}{ln.subproject && <div className="text-[13px] text-[var(--hr-muted)]">{ln.subproject}</div>}</TableCell>
            <TableCell>{categories?.find((c) => c.id === ln.category)?.label ?? ln.category}</TableCell>
            <TableCell>{ln.item}</TableCell>
            <TableCell className="hr-num text-right font-semibold">{qty(ln.quantity, ln.unit)}</TableCell>
            <TableCell>{units?.[ln.unit] ?? ln.unit}</TableCell>
            <TableCell>{ln.employee ?? <span className="text-[var(--hr-muted-2)]">–</span>}</TableCell>
            <OpenCell to={`/vareforbruk${carry({ rediger: ln.entryId })}`} testId={`report-entry-open-${ln.entryId}-${i}`} />
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

const serviceText = (s) => serviceLine(s);

/** Åpne saker i maskinrapporten – klikkbare, åpner saksdetaljen med samme status som i Maskiner. */
function OpenCasesCell({ events, onOpen, machineId }) {
  if (!events.length) return <span className="text-[var(--hr-muted-2)]">0</span>;
  const shown = events.slice(0, 2);
  return (
    <div className="space-y-0.5">
      {shown.map((ev) => (
        <button key={ev.id} type="button" onClick={() => onOpen(ev.id)} className="flex max-w-[260px] items-center gap-1.5 rounded-[6px] text-left text-[13px] hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--hr-focus)] print:hidden" data-testid={`report-case-${ev.id}`}>
          <span className={cn('h-1.5 w-1.5 shrink-0 rounded-full', ev.blocksUse ? 'bg-[var(--hr-red)]' : ev.overdue ? 'bg-[var(--hr-amber)]' : 'bg-[var(--hr-navy)]')} aria-hidden="true" />
          <span className="truncate text-[var(--hr-text)]">{ev.title}</span>
          <span className="shrink-0 text-[var(--hr-muted-2)]">· {ev.statusLabel}</span>
        </button>
      ))}
      {shown.map((ev) => (
        <div key={`p-${ev.id}`} className="hidden text-[13px] print:block">{ev.title} · {ev.statusLabel}</div>
      ))}
      {events.length > 2 && <Link to={`/maskiner/${machineId}?fane=maskinpark`} className="text-[13px] text-[var(--hr-muted)] hover:underline">+{events.length - 2} til</Link>}
    </div>
  );
}

function MaskinerSummary({ r, carry, onOpenCase }) {
  const t = r.totals;
  return (
    <Table data-testid="report-summary-table">
      <TableHeader>
        <TableRow>
          <TableHead>Maskin</TableHead>
          <TableHead>Plassering</TableHead>
          <TableHead className="text-right">Maskintimer i perioden (t)</TableHead>
          <TableHead className="text-right">Føringer</TableHead>
          <TableHead>Prosjekter</TableHead>
          <TableHead className="text-right">Timeteller (register)</TableHead>
          <TableHead>Servicestatus</TableHead>
          <TableHead>Åpne saker</TableHead>
          <TableHead className="print:hidden" />
        </TableRow>
      </TableHeader>
      <TableBody>
        {r.rows.map((m) => (
          <TableRow key={m.key} data-testid={`report-group-${m.key}`}>
            <TableCell className="whitespace-nowrap font-medium text-[var(--hr-heading)]">
              <Link to={`/maskiner/${m.machineId}`} className="hover:underline">{m.label}</Link>
              <div className="text-[13px] text-[var(--hr-muted)]">{m.type}{m.modelYear ? ` · ${m.modelYear}` : ''}{m.responsible ? ` · ${m.responsible.name}` : ''}</div>
              {m.operational && m.operational.status !== 'drift' && <OperationalPill status={m.operational.status} label={m.operational.label} className="mt-1" data-testid={`report-operational-${m.machineId}`} />}
            </TableCell>
            <TableCell>{m.location ?? <span className="text-[var(--hr-muted-2)]">–</span>}</TableCell>
            <TableCell className="hr-num text-right font-semibold">{m.count ? num(m.machineHours) : <span className="text-[var(--hr-muted-2)]">–</span>}</TableCell>
            <TableCell className="hr-tabular text-right">{m.count || <span className="text-[var(--hr-muted-2)]">0</span>}</TableCell>
            <TableCell className="text-[13px]">{m.byProject.length ? m.byProject.map((p) => `${p.label} ${hoursText(p.machineHours)}`).join(' · ') : <span className="text-[var(--hr-muted-2)]">–</span>}</TableCell>
            <TableCell className="hr-tabular text-right">
              {m.meter === null || m.meter === undefined ? <span className="text-[var(--hr-amber)]">mangler</span> : `${fmtNumber(m.meter, 0)} ${m.meterType ?? 'h'}`}
              <div className="text-[12px] text-[var(--hr-muted-2)]">{m.meterUpdatedAt ? `oppdatert ${formatDateShort(m.meterUpdatedAt)}` : 'ikke datert'}</div>
            </TableCell>
            <TableCell>
              <ServiceStatusPill status={m.service.status} data-testid={`report-service-${m.machineId}`} />
              <div className={cn('mt-0.5 text-[12px]', m.service.status === 'ukjent' ? 'text-[var(--hr-amber)]' : 'text-[var(--hr-muted-2)]')}>{serviceText(m.service)}</div>
            </TableCell>
            <TableCell><OpenCasesCell events={m.openEvents} onOpen={onOpenCase} machineId={m.machineId} /></TableCell>
            <OpenCell to={`/timer${carry({ machineId: m.machineId })}`} testId={`report-open-${m.key}`} />
          </TableRow>
        ))}
        <TableRow className="bg-[var(--hr-surface-2)] font-semibold hover:bg-[var(--hr-surface-2)]" data-testid="report-summary-total">
          <TableCell className="text-[var(--hr-heading)]">Sum</TableCell>
          <TableCell className="text-[var(--hr-muted)]">{t.withHours} av {t.machines} med timer</TableCell>
          <TableCell className="hr-num text-right">{num(t.machineHours)}</TableCell>
          <TableCell className="hr-tabular text-right">{t.count}</TableCell>
          <TableCell />
          <TableCell className="hr-tabular text-right text-[var(--hr-muted)]">summeres ikke</TableCell>
          <TableCell className="text-[13px]">{Object.entries(t.serviceStatus).map(([k, v]) => `${v} ${SERVICE_STATUS_LABELS[k]?.toLowerCase() ?? k}`).join(' · ')}</TableCell>
          <TableCell className="hr-tabular">{t.openEvents}{t.blocked ? ` · ${t.blocked} kan ikke brukes` : ''}{t.awaitingClearance ? ` · ${t.awaitingClearance} avventer frigivelse` : ''}</TableCell>
          <TableCell className="print:hidden" />
        </TableRow>
      </TableBody>
    </Table>
  );
}

function MaskinerDetail({ r, carry }) {
  const rows = r.rows.flatMap((m) => m.entries.map((e) => ({ ...e, machineLabel: m.label }))).sort((a, b) => (a.date < b.date ? 1 : -1));
  if (!rows.length) return <p className="px-5 py-4 text-[14px] text-[var(--hr-muted)]">Ingen maskintimer ført i utvalget.</p>;
  return (
    <Table data-testid="report-detail-table">
      <TableHeader>
        <TableRow>
          <TableHead>Dato</TableHead>
          <TableHead>Maskin</TableHead>
          <TableHead>Medarbeider</TableHead>
          <TableHead>Prosjekt</TableHead>
          <TableHead>Aktivitet</TableHead>
          <TableHead className="text-right">Maskintimer (t)</TableHead>
          <TableHead className="text-right">Arbeidstimer (t)</TableHead>
          <TableHead>Status</TableHead>
          <TableHead className="print:hidden" />
        </TableRow>
      </TableHeader>
      <TableBody>
        {rows.map((e) => (
          <TableRow key={e.id}>
            <TableCell className="hr-tabular whitespace-nowrap">{formatDateShort(e.date)}</TableCell>
            <TableCell className="font-medium text-[var(--hr-heading)]">{e.machineLabel}</TableCell>
            <TableCell>{e.employee?.name}</TableCell>
            <TableCell>{e.project?.name}</TableCell>
            <TableCell>{e.activity?.label}</TableCell>
            <TableCell className="hr-num text-right font-semibold">{num(e.machineTotalHours)}</TableCell>
            <TableCell className="hr-num text-right">{num(e.hours)}</TableCell>
            <TableCell><TimeStatusPill status={e.status} /></TableCell>
            <OpenCell to={`/timer${carry({ rediger: e.id })}`} testId={`report-entry-open-${e.id}`} />
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

/* ---------- Hendelser og service ---------- */

function HendelserSummary({ r, onOpenCase }) {
  const t = r.totals;
  const link = (g) => (r.groupBy === 'maskin' ? `/maskiner/${g.key}?fane=maskinpark` : null);
  return (
    <Table data-testid="report-summary-table">
      <TableHeader>
        <TableRow>
          <TableHead>{r.groupLabel}</TableHead>
          <TableHead>Detalj</TableHead>
          <TableHead className="text-right">Saker</TableHead>
          <TableHead className="text-right">Åpne</TableHead>
          <TableHead className="text-right">Avsluttet</TableHead>
          <TableHead className="text-right">Feil</TableHead>
          <TableHead className="text-right">Service</TableHead>
          <TableHead className="whitespace-nowrap text-right">Kan ikke brukes</TableHead>
          <TableHead className="whitespace-nowrap text-right">Forfalt frist</TableHead>
          <TableHead className="print:hidden" />
        </TableRow>
      </TableHeader>
      <TableBody>
        {r.groups.map((g) => (
          <TableRow key={g.key || 'none'} data-testid={`report-group-${g.key || 'none'}`}>
            <TableCell className="font-medium text-[var(--hr-heading)]">{g.label}</TableCell>
            <TableCell className="text-[var(--hr-muted)]">{g.sublabel ?? ''}</TableCell>
            <TableCell className="hr-tabular text-right font-semibold">{g.count}</TableCell>
            <TableCell className={cn('hr-tabular text-right', g.open ? 'text-[var(--hr-heading)]' : 'text-[var(--hr-muted-2)]')}>{g.open}</TableCell>
            <TableCell className="hr-tabular text-right text-[var(--hr-muted)]">{g.resolved}</TableCell>
            <TableCell className="hr-tabular text-right">{g.faults || <span className="text-[var(--hr-muted-2)]">–</span>}</TableCell>
            <TableCell className="hr-tabular text-right">{g.service || <span className="text-[var(--hr-muted-2)]">–</span>}</TableCell>
            <TableCell className={cn('hr-tabular text-right', g.blocked ? 'font-semibold text-[var(--hr-red)]' : 'text-[var(--hr-muted-2)]')}>{g.blocked}</TableCell>
            <TableCell className={cn('hr-tabular text-right', g.overdue ? 'font-semibold text-[var(--hr-amber)]' : 'text-[var(--hr-muted-2)]')}>{g.overdue}</TableCell>
            {link(g) ? (
              <OpenCell to={link(g)} testId={`report-open-${g.key || 'none'}`} />
            ) : (
              <TableCell className="w-[1%] whitespace-nowrap text-right print:hidden">
                <button type="button" onClick={() => onOpenCase(g.events[0].id)} className="inline-flex h-8 items-center gap-1 rounded-[8px] px-2 text-[13px] font-medium text-[var(--hr-accent-text)] hover:bg-[var(--hr-surface-2)] hover:underline" data-testid={`report-open-${g.key || 'none'}`}>
                  Første sak <ArrowUpRight className="h-3.5 w-3.5" aria-hidden="true" />
                </button>
              </TableCell>
            )}
          </TableRow>
        ))}
        <TableRow className="bg-[var(--hr-surface-2)] font-semibold hover:bg-[var(--hr-surface-2)]" data-testid="report-summary-total">
          <TableCell className="text-[var(--hr-heading)]">Sum</TableCell>
          <TableCell className="text-[var(--hr-muted)]">{t.machines} maskiner</TableCell>
          <TableCell className="hr-tabular text-right">{t.count}</TableCell>
          <TableCell className="hr-tabular text-right">{t.open}</TableCell>
          <TableCell className="hr-tabular text-right">{t.resolved}</TableCell>
          <TableCell className="hr-tabular text-right">{t.faults}</TableCell>
          <TableCell className="hr-tabular text-right">{t.service}</TableCell>
          <TableCell className="hr-tabular text-right">{t.blocked}</TableCell>
          <TableCell className="hr-tabular text-right">{t.overdue}</TableCell>
          <TableCell className="print:hidden" />
        </TableRow>
      </TableBody>
    </Table>
  );
}

function HendelserDetail({ r, onOpenCase }) {
  const rows = r.groups.flatMap((g) => g.events).sort((a, b) => (a.date < b.date ? 1 : a.date > b.date ? -1 : 0));
  if (!rows.length) return <p className="px-5 py-4 text-[14px] text-[var(--hr-muted)]">Ingen saker i utvalget.</p>;
  return (
    <Table data-testid="report-detail-table">
      <TableHeader>
        <TableRow>
          <TableHead>Dato</TableHead>
          <TableHead>Maskin</TableHead>
          <TableHead>Sak</TableHead>
          <TableHead>Status</TableHead>
          <TableHead>Tildelt</TableHead>
          <TableHead>Frist</TableHead>
          <TableHead>Løst / utført</TableHead>
          <TableHead>Utført arbeid</TableHead>
          <TableHead className="print:hidden" />
        </TableRow>
      </TableHeader>
      <TableBody>
        {rows.map((e) => (
          <TableRow key={e.id} data-testid={`report-case-row-${e.id}`}>
            <TableCell className="hr-tabular whitespace-nowrap">{formatDateShort(e.date)}</TableCell>
            <TableCell className="whitespace-nowrap font-medium text-[var(--hr-heading)]">{e.machine ? `${e.machine.internalNo} · ${e.machine.name}` : e.machineId}</TableCell>
            <TableCell className="max-w-[320px]">
              <div className="text-[13px] text-[var(--hr-muted)]">{EVENT_TYPE_LABELS[e.type] ?? e.type}{e.project ? ` · ${e.project.name}` : ''}</div>
              <div className="font-medium text-[var(--hr-text)]">{e.title}</div>
            </TableCell>
            <TableCell><CaseBadges ev={e} /></TableCell>
            <TableCell className="whitespace-nowrap">{e.assignedTo?.name ?? <span className="text-[var(--hr-amber)]">Ikke tildelt</span>}</TableCell>
            <TableCell className={cn('hr-tabular whitespace-nowrap', e.overdue && 'font-semibold text-[var(--hr-amber)]')}>{e.dueDate ? formatDateShort(e.dueDate) : <span className="text-[var(--hr-muted-2)]">–</span>}</TableCell>
            <TableCell className="hr-tabular whitespace-nowrap">{e.resolution ? `${formatDateShort(e.resolution.date)}${e.resolution.by ? ` · ${e.resolution.by.name}` : ''}` : <span className="text-[var(--hr-muted-2)]">–</span>}</TableCell>
            <TableCell className="max-w-[320px] text-[13px]">{e.resolution?.work ?? <span className="text-[var(--hr-muted-2)]">–</span>}</TableCell>
            <TableCell className="w-[1%] whitespace-nowrap text-right print:hidden">
              <button type="button" onClick={() => onOpenCase(e.id)} className="inline-flex h-8 items-center gap-1 rounded-[8px] px-2 text-[13px] font-medium text-[var(--hr-accent-text)] hover:bg-[var(--hr-surface-2)] hover:underline" data-testid={`report-case-open-${e.id}`}>
                Åpne <ArrowUpRight className="h-3.5 w-3.5" aria-hidden="true" />
              </button>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

/* ---------- HMS, RUH og kvalitetsavvik ---------- */

/** Lenke per gruppe: samme utvalg i avvikslisten der listen har filteret; ellers første sak. */
function avvikGroupLink(f, groupBy, g) {
  if (groupBy === 'prosjekt') return { to: hmsListLink(f, { projectId: g.key }) };
  if (groupBy === 'type') return { to: hmsListLink(f, { kind: g.key }) };
  if (groupBy === 'status') return { to: hmsListLink(f, { devStatus: g.key, visning: 'alle' }) };
  if (groupBy === 'alvorlighet') return { to: hmsListLink(f, { severity: g.key }) };
  if (groupBy === 'ansvarlig' && g.key) return { to: hmsListLink(f, { responsibleId: g.key }) };
  const first = g.deviations?.[0];
  return first ? { to: deviationPath(first.id), first: true } : {};
}

function AvvikSummary({ r, f, from }) {
  const t = r.totals;
  return (
    <Table data-testid="report-summary-table">
      <TableHeader>
        <TableRow>
          <TableHead>{r.groupLabel}</TableHead>
          <TableHead>Detalj</TableHead>
          <TableHead className="text-right">Avvik</TableHead>
          <TableHead className="text-right">Åpne</TableHead>
          <TableHead className="text-right">Lukket</TableHead>
          <TableHead className="whitespace-nowrap text-right">Forfalt frist</TableHead>
          <TableHead className="text-right">RUH</TableHead>
          <TableHead className="text-right">Kvalitet</TableHead>
          <TableHead className="whitespace-nowrap text-right">Høy/kritisk</TableHead>
          <TableHead className="whitespace-nowrap text-right">Åpne tiltak</TableHead>
          <TableHead className="print:hidden" />
        </TableRow>
      </TableHeader>
      <TableBody>
        {r.groups.map((g) => {
          const link = avvikGroupLink(f, r.groupBy, g);
          return (
            <TableRow key={g.key || 'none'} data-testid={`report-group-${g.key || 'none'}`}>
              <TableCell className="font-medium text-[var(--hr-heading)]">{g.label}</TableCell>
              <TableCell className="text-[var(--hr-muted)]">{g.sublabel ?? ''}</TableCell>
              <TableCell className="hr-tabular text-right font-semibold">{g.count}</TableCell>
              <TableCell className={cn('hr-tabular text-right', g.open ? 'text-[var(--hr-heading)]' : 'text-[var(--hr-muted-2)]')}>{g.open}</TableCell>
              <TableCell className="hr-tabular text-right text-[var(--hr-muted)]">{g.closed}</TableCell>
              <TableCell className={cn('hr-tabular text-right', g.overdue ? 'font-semibold text-[var(--hr-amber)]' : 'text-[var(--hr-muted-2)]')}>{g.overdue}</TableCell>
              <TableCell className="hr-tabular text-right">{g.ruh || <span className="text-[var(--hr-muted-2)]">–</span>}</TableCell>
              <TableCell className="hr-tabular text-right">{g.kvalitet || <span className="text-[var(--hr-muted-2)]">–</span>}</TableCell>
              <TableCell className={cn('hr-tabular text-right', g.high ? 'font-semibold text-[var(--hr-red)]' : 'text-[var(--hr-muted-2)]')}>{g.high}</TableCell>
              <TableCell className={cn('hr-tabular text-right', g.actionsOverdue ? 'font-semibold text-[var(--hr-amber)]' : g.actionsOpen ? '' : 'text-[var(--hr-muted-2)]')}>
                {g.actionsOpen}
                {g.actionsOverdue ? <span className="ml-1 text-[12px]">({g.actionsOverdue} forfalt)</span> : null}
              </TableCell>
              {link.to ? (
                <TableCell className="w-[1%] whitespace-nowrap text-right">
                  <Link to={link.to} state={link.first ? { from } : undefined} className="inline-flex h-8 items-center gap-1 rounded-[8px] px-2 text-[13px] font-medium text-[var(--hr-accent-text)] hover:bg-[var(--hr-surface-2)] hover:underline print:hidden" data-testid={`report-open-${g.key || 'none'}`}>
                    {link.first ? 'Første sak' : 'Åpne'} <ArrowUpRight className="h-3.5 w-3.5" aria-hidden="true" />
                  </Link>
                </TableCell>
              ) : (
                <TableCell className="print:hidden" />
              )}
            </TableRow>
          );
        })}
        <TableRow className="bg-[var(--hr-surface-2)] font-semibold hover:bg-[var(--hr-surface-2)]" data-testid="report-summary-total">
          <TableCell className="text-[var(--hr-heading)]">Sum</TableCell>
          <TableCell className="text-[var(--hr-muted)]">{t.projects} prosjekter</TableCell>
          <TableCell className="hr-tabular text-right">{t.count}</TableCell>
          <TableCell className="hr-tabular text-right">{t.open}</TableCell>
          <TableCell className="hr-tabular text-right">{t.closed}</TableCell>
          <TableCell className="hr-tabular text-right">{t.overdue}</TableCell>
          <TableCell className="hr-tabular text-right">{t.ruh}</TableCell>
          <TableCell className="hr-tabular text-right">{t.kvalitet}</TableCell>
          <TableCell className="hr-tabular text-right">{(t.bySeverity?.hoy ?? 0) + (t.bySeverity?.kritisk ?? 0)}</TableCell>
          <TableCell className="hr-tabular text-right">{t.actions?.open ?? 0}</TableCell>
          <TableCell className="print:hidden" />
        </TableRow>
      </TableBody>
    </Table>
  );
}

function AvvikDetail({ r, from }) {
  const rows = r.deviations ?? [];
  if (!rows.length) return <p className="px-5 py-4 text-[14px] text-[var(--hr-muted)]">Ingen avvik i utvalget.</p>;
  return (
    <Table data-testid="report-detail-table">
      <TableHeader>
        <TableRow>
          <TableHead>Dato</TableHead>
          <TableHead>Avvik</TableHead>
          <TableHead>Prosjekt</TableHead>
          <TableHead>Status</TableHead>
          <TableHead>Kategori · årsak</TableHead>
          <TableHead>Ansvarlig</TableHead>
          <TableHead>Frist</TableHead>
          <TableHead className="text-right">Tiltak</TableHead>
          <TableHead>Lukket</TableHead>
          <TableHead className="print:hidden" />
        </TableRow>
      </TableHeader>
      <TableBody>
        {rows.map((d) => (
          <TableRow key={d.id} data-testid={`report-dev-row-${d.id}`}>
            <TableCell className="hr-tabular whitespace-nowrap">{formatDateShort(d.date)}{d.time ? <div className="text-[12px] text-[var(--hr-muted)]">kl. {d.time}</div> : null}</TableCell>
            <TableCell className="max-w-[320px]">
              <div className="flex items-center gap-1.5"><DeviationKindMark kind={d.kind} /><span className="font-medium text-[var(--hr-text)]">{d.title}</span></div>
              {d.immediateAction && <div className="mt-0.5 line-clamp-1 text-[13px] text-[var(--hr-muted)]">Straks: {d.immediateAction}</div>}
            </TableCell>
            <TableCell className="max-w-[200px]"><div className="truncate font-medium text-[var(--hr-heading)]">{d.project?.name ?? d.projectId}</div>{d.subproject && <div className="truncate text-[13px] text-[var(--hr-muted)]">{d.subproject.name}</div>}</TableCell>
            <TableCell><DeviationBadges d={d} compact /></TableCell>
            <TableCell className="max-w-[220px] text-[13px]">
              <div className={cn(!isClarified(d.category) && 'text-[var(--hr-amber)]')}>{d.categoryLabel}</div>
              <div className={cn('text-[var(--hr-muted)]', !isClarified(d.cause) && 'text-[var(--hr-amber)]')}>{isClarified(d.cause) ? d.causeLabel : 'Årsak ikke avklart'}</div>
            </TableCell>
            <TableCell className="whitespace-nowrap">{d.responsible?.name ?? <span className="text-[var(--hr-amber)]">Ikke tildelt</span>}</TableCell>
            <TableCell className={cn('hr-tabular whitespace-nowrap', d.overdue && 'font-semibold text-[var(--hr-amber)]')}>{d.dueDate ? formatDateShort(d.dueDate) : <span className="text-[var(--hr-muted-2)]">–</span>}</TableCell>
            <TableCell className={cn('hr-tabular whitespace-nowrap text-right', d.actionsOverdue && 'font-semibold text-[var(--hr-amber)]')}>{d.actions?.length ? `${d.actionsDone}/${d.actions.length}` : <span className="text-[var(--hr-muted-2)]">–</span>}</TableCell>
            <TableCell className="max-w-[260px] text-[13px]">{d.closure ? <><span className="hr-tabular whitespace-nowrap">{formatDateShort(d.closure.date)}{d.closure.by ? ` · ${d.closure.by.name}` : ''}</span><div className="line-clamp-2 text-[var(--hr-muted)]">{d.closure.conclusion}</div></> : <span className="text-[var(--hr-muted-2)]">–</span>}</TableCell>
            <TableCell className="w-[1%] whitespace-nowrap text-right print:hidden">
              <Link to={deviationPath(d.id)} state={{ from }} className="inline-flex h-8 items-center gap-1 rounded-[8px] px-2 text-[13px] font-medium text-[var(--hr-accent-text)] hover:bg-[var(--hr-surface-2)] hover:underline" data-testid={`report-dev-open-${d.id}`}>
                Åpne <ArrowUpRight className="h-3.5 w-3.5" aria-hidden="true" />
              </Link>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

/** Fordeling per kategori og årsak – tellinger og åpne, med rolig stolpe. Ingen kostnader eller gevinst. */
function DistributionList({ title, items, testId }) {
  const max = Math.max(1, ...items.map((i) => i.count));
  return (
    <div className="min-w-0 px-5 py-4" data-testid={testId}>
      <h3 className="text-[13px] font-semibold uppercase tracking-[0.06em] text-[var(--hr-muted-2)]">{title}</h3>
      {items.length === 0 ? (
        <p className="mt-2 text-[13px] text-[var(--hr-muted)]">Ingen i utvalget.</p>
      ) : (
        <ul className="mt-2 space-y-2">
          {items.map((it) => (
            <li key={it.key} data-testid={`${testId}-${it.key}`}>
              <div className="flex items-baseline justify-between gap-3 text-[14px]">
                <span className={cn('min-w-0 truncate', it.key === NOT_CLARIFIED ? 'text-[var(--hr-amber)]' : 'text-[var(--hr-text)]')}>{it.label}</span>
                <span className="hr-tabular shrink-0 text-[var(--hr-heading)]">
                  <span className="font-semibold">{it.count}</span>
                  {it.open ? <span className="text-[13px] text-[var(--hr-muted)]"> · {it.open} åpne</span> : null}
                </span>
              </div>
              <div className="mt-1 h-1.5 w-full overflow-hidden rounded-full bg-[var(--hr-inset)]" aria-hidden="true">
                <div className={cn('h-full rounded-full', it.key === NOT_CLARIFIED ? 'bg-[var(--hr-amber)]' : 'bg-[var(--hr-navy)]')} style={{ width: `${Math.max(4, Math.round((it.count / max) * 100))}%` }} />
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function AvvikDistribution({ r }) {
  const t = r.totals;
  return (
    <div className="grid divide-y divide-[var(--hr-hairline)] md:grid-cols-3 md:divide-x md:divide-y-0" data-testid="report-distribution">
      <DistributionList title={`RUH – type hendelse (${t.ruh})`} items={t.categoriesRuh ?? []} testId="report-dist-ruh" />
      <DistributionList title={`Kvalitet – relatert til (${t.kvalitet})`} items={t.categoriesQuality ?? []} testId="report-dist-quality" />
      <DistributionList title={`Årsak (${t.count})`} items={t.causes ?? []} testId="report-dist-cause" />
    </div>
  );
}

/* ---------- Nøkkeltall øverst (få, meningsfulle) ---------- */

function Totals({ type, r, units, f }) {
  const t = r.totals;
  if (type === 'avvik') {
    const ll = 'Åpne i avvikslisten';
    return (
      <>
        <LinkStat label="Avvik i perioden" value={fmtNumber(t.count, 0)} meta={`${t.ruh} RUH · ${t.kvalitet} kvalitet · ${t.projects} prosjekter`} to={hmsListLink(f, { visning: 'alle' })} linkLabel={ll} testId="report-total-deviations" />
        <LinkStat label="Åpne" value={fmtNumber(t.open, 0)} meta={t.overdue ? `${t.overdue} med passert frist` : 'Ingen passerte frister'} tone={t.overdue ? 'amber' : undefined} to={hmsListLink(f, { visning: 'apne' })} linkLabel={ll} testId="report-total-open" />
        <LinkStat label="Forfalt" value={fmtNumber(t.overdue, 0)} meta={t.actions?.overdue ? `${t.actions.overdue} forfalte tiltak` : `${t.actions?.open ?? 0} åpne tiltak`} tone={t.overdue || t.actions?.overdue ? 'amber' : undefined} to={hmsListLink(f, { visning: 'forfalt' })} linkLabel={ll} testId="report-total-overdue" />
        <LinkStat label="Lukket" value={fmtNumber(t.closed, 0)} meta={t.avgCloseDays === null || t.avgCloseDays === undefined ? 'Ingen lukkede i utvalget' : `snitt ${fmtNumber(t.avgCloseDays, 1)} dager til lukket`} to={hmsListLink(f, { visning: 'lukket' })} linkLabel={ll} testId="report-total-closed" />
      </>
    );
  }
  if (type === 'timer') {
    return (
      <>
        <MiniStat label="Arbeidstimer" value={hoursText(t.hours)} meta={`${t.count} ${t.count === 1 ? 'føring' : 'føringer'} · ${t.employees} medarbeidere`} testId="report-total-hours" />
        <MiniStat label="Maskintimer" value={hoursText(t.machineHours)} meta="Registrert i timeføring" testId="report-total-machine" />
        <MiniStat label="Til godkjenning" value={fmtNumber(t.byStatus.sendt ?? 0, 0)} meta={statusBreakdown(t.byStatus) || 'Ingen føringer'} tone={t.byStatus.sendt ? 'amber' : undefined} testId="report-total-pending" />
        <MiniStat label="Tillegg" value={t.supplements.length ? t.supplements.map((s) => `${fmtNumber(s.quantity, s.unit === 'stk' ? 0 : 1)} ${s.unit}`).join(' · ') : '–'} meta={t.supplements.map((s) => s.label).join(' · ') || 'Ingen tillegg'} testId="report-total-supplements" />
      </>
    );
  }
  if (type === 'prosjektforbruk') {
    return (
      <>
        <MiniStat label="Prosjekter" value={fmtNumber(t.projects, 0)} meta="Med føringer i perioden" testId="report-total-projects" />
        <MiniStat label="Arbeidstimer" value={hoursText(t.hours)} meta={`${t.timeCount} føringer`} testId="report-total-hours" />
        <MiniStat label="Maskintimer" value={hoursText(t.machineHours)} meta="Registrert i timeføring" testId="report-total-machine" />
        <MiniStat label="Vareforbruk" value={byUnitText(t.consumptionByUnit, units) ?? '–'} meta={`${t.consumptionLines} varelinjer · per enhet`} testId="report-total-consumption" />
      </>
    );
  }
  if (type === 'vareforbruk') {
    return (
      <>
        <MiniStat label="Varelinjer" value={fmtNumber(t.lineCount, 0)} meta={`${t.count} registreringer`} testId="report-total-lines" />
        <MiniStat label="Sum per enhet" value={byUnitText(t.byUnit, units) ?? '–'} meta="Tonn, meter og stk hver for seg" testId="report-total-units" />
        <MiniStat label="Varer" value={fmtNumber(t.items, 0)} meta="Ulike varer i utvalget" testId="report-total-items" />
        <MiniStat label="Prosjekter" value={fmtNumber(t.projects, 0)} meta="Med forbruk i perioden" testId="report-total-projects" />
      </>
    );
  }
  if (type === 'hendelser') {
    return (
      <>
        <MiniStat label="Saker i perioden" value={fmtNumber(t.count, 0)} meta={`${t.faults} feil · ${t.service} service/vedlikehold · ${t.machines} maskiner`} testId="report-total-cases" />
        <MiniStat label="Åpne" value={fmtNumber(t.open, 0)} meta={`${t.resolved} avsluttet${t.overdue ? ` · ${t.overdue} forfalt frist` : ''}`} tone={t.overdue ? 'amber' : undefined} testId="report-total-open" />
        <MiniStat label="Kan ikke brukes" value={fmtNumber(t.blocked, 0)} meta={t.awaitingClearance ? `${t.awaitingClearance} avventer frigivelse` : 'Ingen avventer frigivelse'} tone={t.blocked || t.awaitingClearance ? 'amber' : undefined} testId="report-total-blocked" />
        <MiniStat label="Tid til løst (feil)" value={t.avgResolveDays === null || t.avgResolveDays === undefined ? '–' : `${fmtNumber(t.avgResolveDays, 1)} dager`} meta={t.resolvedFaults ? `snitt for ${t.resolvedFaults} løste feil` : 'Ingen løste feil i utvalget'} testId="report-total-resolve" />
      </>
    );
  }
  const st = t.serviceStatus ?? {};
  return (
    <>
      <MiniStat label="Maskintimer i perioden" value={hoursText(t.machineHours)} meta={`${t.count} føringer med maskin`} testId="report-total-machine" />
      <MiniStat label="Maskiner i bruk" value={`${t.withHours} av ${t.machines}`} meta="Med registrerte timer" testId="report-total-machines" />
      <MiniStat label="Servicestatus" value={`${st.forfalt ?? 0} forfalt · ${st.snart ?? 0} snart`} meta={`${st.ok ?? 0} OK · ${st.ukjent ?? 0} ukjent`} tone={st.forfalt || st.ukjent ? 'amber' : undefined} testId="report-total-service" />
      <MiniStat label="Åpne saker" value={fmtNumber(t.openEvents, 0)} meta={t.blocked ? `${t.blocked} kan ikke brukes${t.awaitingClearance ? ` · ${t.awaitingClearance} avventer frigivelse` : ''}` : t.awaitingClearance ? `${t.awaitingClearance} avventer frigivelse` : 'Feil, service og vedlikehold'} tone={t.openEvents ? 'amber' : undefined} testId="report-total-events" />
    </>
  );
}

export default function DriftRapportPage() {
  const { type } = useParams();
  const cfg = CONFIG[type];
  const meta = DRIFT_REPORTS.find((r) => r.id === type);
  const { refs } = useDriftRefs();
  const f = useDriftFilters({ keys: cfg?.keys ?? [] });
  const { openCase } = useCaseParam();
  const location = useLocation();
  const [data, setData] = useState(null);
  const [status, setStatus] = useState('loading');
  const [machines, setMachines] = useState([]);
  const [showDetail, setShowDetail] = useState(false);

  const groupBy = f.groupBy ?? cfg?.defaultGroup;
  const queryKey = JSON.stringify({ ...f.query, groupBy: cfg?.defaultGroup ? groupBy : undefined });

  useEffect(() => {
    if (meta) document.title = `${meta.label} · Rapporter · Hardanger Rock`;
  }, [meta]);

  useEffect(() => {
    if (cfg?.more.includes('machineId')) listMachines().then((d) => setMachines(d.machines)).catch(() => {});
  }, [cfg]);

  const load = useCallback(async () => {
    if (!cfg) return;
    setStatus((s) => (s === 'ready' ? 'refreshing' : 'loading'));
    try {
      setData(await getDriftReport(type, JSON.parse(queryKey)));
      setStatus('ready');
    } catch {
      setStatus('error');
    }
  }, [type, queryKey, cfg]);

  useEffect(() => {
    load();
  }, [load]);

  const exportParams = useMemo(() => JSON.parse(queryKey), [queryKey]);

  if (!cfg) return <Navigate to="/rapporter" replace />;

  const units = refs?.units;
  const categories = refs?.categories;
  const hasRows = data && ((data.groups?.length ?? 0) > 0 || (data.rows?.length ?? 0) > 0);
  const detailCount = data ? (type === 'timer' || type === 'hendelser' || type === 'avvik' ? data.totals.count : type === 'vareforbruk' ? data.totals.lineCount : type === 'maskiner' ? data.totals.count : data.rows.reduce((a, p) => a + p.items.length, 0)) : 0;
  /** Saksrommet lenker tilbake hit med samme filtre. */
  const reportFrom = { label: 'rapporten', to: `${location.pathname}${location.search}` };

  return (
    <div className="hr-print-landscape space-y-5" data-testid={`report-${type}`}>
      <div className="print:hidden">
        <PageHeader title={meta.label} lead={meta.description}>
          <Button variant="outline" className="h-10 rounded-[10px] bg-white px-3 text-[14px]" onClick={() => window.print()} disabled={!hasRows} data-testid="report-print-button">
            <Printer className="mr-1.5 h-4 w-4 text-[var(--hr-muted)]" aria-hidden="true" /> Skriv ut / PDF
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" className="h-10 rounded-[10px] bg-white px-3 text-[14px]" disabled={!hasRows} data-testid="report-csv-button">
                <Download className="mr-1.5 h-4 w-4 text-[var(--hr-muted)]" aria-hidden="true" /> CSV <ChevronDown className="ml-1 h-3.5 w-3.5 text-[var(--hr-muted-2)]" aria-hidden="true" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-64 rounded-[12px] p-1.5 shadow-[var(--hr-shadow-md)]">
              <DropdownMenuLabel className="px-2 py-1.5 text-[12px] font-semibold uppercase tracking-[0.08em] text-[var(--hr-muted-2)]">Kun det filtrerte utvalget</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem asChild className="rounded-[8px] py-2">
                <a href={exportUrl(type, 'csv', exportParams)} download data-testid="report-csv-summary">Sammendrag (CSV)</a>
              </DropdownMenuItem>
              <DropdownMenuItem asChild className="rounded-[8px] py-2">
                <a href={exportUrl(type, 'csv', exportParams, { detail: true })} download data-testid="report-csv-detail">{cfg.detailLabel} (CSV)</a>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <Button asChild className={cn('h-10 rounded-[10px] bg-[var(--hr-heading)] px-4 text-[14px] font-semibold text-white shadow-none hover:bg-[var(--hr-navy)]', !hasRows && 'pointer-events-none opacity-50')}>
            <a href={exportUrl(type, 'xlsx', exportParams)} download data-testid="report-xlsx-button" aria-disabled={!hasRows} tabIndex={hasRows ? undefined : -1}>
              <FileSpreadsheet className="mr-1.5 h-4 w-4" aria-hidden="true" /> Excel (XLSX)
            </a>
          </Button>
        </PageHeader>
      </div>

      {/* Utskriftstopp: tittel, periode, filtre, enheter og datagrunnlag */}
      {data && (
        <div className="hidden print:block" data-testid="report-print-header">
          <div className="text-[12px] font-semibold uppercase tracking-[0.14em] text-[var(--hr-accent-text)]">Hardanger Rock · Driftsrapport</div>
          <h2 className="mt-1 text-[24px] font-semibold leading-tight text-[var(--hr-heading)]">{data.title}</h2>
          <dl className="mt-2 grid grid-cols-[max-content_1fr] gap-x-6 gap-y-1 text-[13px]">
            <dt className="text-[var(--hr-muted)]">Periode</dt>
            <dd>{data.period.label}</dd>
            {data.groupLabel && (
              <>
                <dt className="text-[var(--hr-muted)]">Gruppering</dt>
                <dd>{data.groupLabel}</dd>
              </>
            )}
            <dt className="text-[var(--hr-muted)]">Filtre</dt>
            <dd>{data.filters.length ? data.filters.map((x) => `${x.label}: ${x.value}`).join(' · ') : 'Ingen – hele utvalget i perioden'}</dd>
            <dt className="text-[var(--hr-muted)]">Enheter</dt>
            <dd>Timer i t (desimal), tillegg per type med egen enhet, vareforbruk per vare og enhet (tonn, meter, stk, m²) – aldri summert på tvers</dd>
            <dt className="text-[var(--hr-muted)]">Generert</dt>
            <dd>{formatDateTime(data.generatedAt)}</dd>
            <dt className="text-[var(--hr-muted)]">Datagrunnlag</dt>
            <dd>Eksempeldata – fiktive medarbeidere, kunder, prosjekter og registreringer. Ingen kobling til SmartDok eller andre systemer.</dd>
          </dl>
        </div>
      )}

      <div className="print:hidden">
        <ExampleDataNote>{type === 'avvik' ? 'Rapporten viser manuelt meldte RUH og kvalitetsavvik med samme status og tall som i Avvik › HMS / RUH / Kvalitet. Tellerne åpner nøyaktig samme utvalg i listen. Ingen kostnader eller gevinst – kun antall, status, kategorier og tiltak.' : type === 'hendelser' ? 'Rapporten viser saker fra maskinparken med samme status som i Maskiner. «Åpne» viser saksdetaljen.' : 'Rapporten viser tid og mengder fra eksempelregistreringene – ingen inntekter, satser eller fakturagrunnlag.'}</ExampleDataNote>
      </div>

      <Panel className="min-w-0 print:hidden">
        <DriftFilterBar f={f} refs={refs} machines={machines} more={cfg.more} testPrefix="report" className="border-b-0">
          {cfg.defaultGroup && data?.groupings && (
            <div className="flex w-full items-center gap-2 sm:w-auto">
              <span className="whitespace-nowrap text-[13px] text-[var(--hr-muted)]">Grupper etter</span>
              <SelectField value={groupBy} onValueChange={(g) => f.setGroupBy(g === cfg.defaultGroup ? '' : g)} options={data.groupings.map((g) => ({ value: g.id, label: g.label }))} className="h-9 w-full lg:h-9 sm:w-[170px]" testId="report-groupby" />
            </div>
          )}
        </DriftFilterBar>
      </Panel>

      {status === 'loading' && !data && <SkeletonTable rows={8} />}
      {status === 'error' && (
        <Note tone="amber" data-testid="report-load-error">
          Kunne ikke hente rapporten. <Button variant="link" className="h-auto px-1" onClick={load} data-testid="report-load-retry">Prøv igjen</Button>
        </Note>
      )}

      {data && (
        <>
          <Panel className={cn(status === 'refreshing' && 'opacity-70 transition-opacity duration-150')}>
            <PanelHeader title={`Sum for ${data.period.label.toLowerCase()}`} description={data.filters.length ? `Aktive filtre: ${data.filters.map((x) => `${x.label} ${x.value}`).join(' · ')}` : 'Hele utvalget i perioden.'} />
            <div className="grid grid-cols-2 divide-x divide-[var(--hr-hairline)] lg:grid-cols-4" data-testid="report-totals">
              <Totals type={type} r={data} units={units} f={f} />
            </div>
          </Panel>

          <Panel className="min-w-0">
            <PanelHeader title="Sammendrag" description={type === 'avvik' ? `Gruppert per ${data.groupLabel.toLowerCase()}. «Åpne» viser samme utvalg i avvikslisten; fordeling og sakstabell ligger under.` : type === 'maskiner' ? 'Registrert maskintid fra timeføring. Registerets timeteller er livstid og summeres aldri. Åpne saker kan klikkes.' : type === 'hendelser' ? `Gruppert per ${data.groupLabel.toLowerCase()}. Detaljlisten under viser hver sak med saksgang og utført arbeid.` : type === 'prosjektforbruk' ? 'Per prosjekt. Åpne prosjektet for underprosjekter og registreringer.' : `Gruppert per ${data.groupLabel.toLowerCase()}. «Åpne» viser registreringene bak tallet med samme filter.`} />
            {!hasRows ? (
              <div className="p-5">
                <EmptyState title={type === 'avvik' ? 'Ingen avvik i utvalget' : 'Ingen registreringer i utvalget'} description={type === 'avvik' ? 'Ingen RUH eller kvalitetsavvik med hendelsesdato i perioden og valgte filtre.' : 'Endre periode eller filtre.'} onReset={f.activeCount || f.period.preset !== '14' ? f.reset : undefined} />
              </div>
            ) : (
              <div className="hr-scroll overflow-x-auto" data-testid="report-summary-scroll">
                {type === 'timer' && <TimerSummary r={data} carry={f.carry} />}
                {type === 'prosjektforbruk' && <ProsjektforbrukSummary r={data} carry={f.carry} units={units} />}
                {type === 'vareforbruk' && <VareforbrukSummary r={data} carry={f.carry} units={units} />}
                {type === 'maskiner' && <MaskinerSummary r={data} carry={f.carry} onOpenCase={openCase} />}
                {type === 'hendelser' && <HendelserSummary r={data} onOpenCase={openCase} />}
                {type === 'avvik' && <AvvikSummary r={data} f={f} from={reportFrom} />}
              </div>
            )}
          </Panel>

          {type === 'avvik' && hasRows && (
            <Panel className="min-w-0" data-testid="report-distribution-panel">
              <PanelHeader title="Fordeling" description="Kategorier og årsaker i utvalget. «Ikke avklart» vises ærlig – det er saker der årsaken ikke er undersøkt ennå." />
              <AvvikDistribution r={data} />
            </Panel>
          )}

          {hasRows && (
            <Collapsible open={showDetail} onOpenChange={setShowDetail}>
              <Panel className={cn('min-w-0', !showDetail && 'print:hidden')}>
                <div className="flex flex-col gap-2 px-5 py-3.5 sm:flex-row sm:items-center sm:justify-between">
                  <div className="min-w-0">
                    <h2 className="text-[16px] font-semibold leading-6 tracking-[-0.01em] text-[var(--hr-heading)]">{cfg.detailLabel}</h2>
                    <p className="mt-0.5 text-[14px] leading-5 text-[var(--hr-muted)]">{detailCount} {detailCount === 1 ? 'rad' : 'rader'} i utvalget. Tas med i utskrift når listen er åpen.</p>
                  </div>
                  <CollapsibleTrigger asChild>
                    <Button variant="outline" size="sm" className="h-8 shrink-0 rounded-[8px] bg-white text-[13px] print:hidden" data-testid="report-detail-toggle">
                      <ChevronDown className={cn('mr-1 h-3.5 w-3.5 transition-transform duration-150', showDetail && 'rotate-180')} aria-hidden="true" />
                      {showDetail ? 'Skjul' : 'Vis'} {cfg.detailLabel.toLowerCase()}
                    </Button>
                  </CollapsibleTrigger>
                </div>
                <CollapsibleContent>
                  <div className="hr-scroll overflow-x-auto border-t border-[var(--hr-hairline)]" data-testid="report-detail-scroll">
                    {type === 'timer' && <TimerDetail r={data} carry={f.carry} />}
                    {type === 'prosjektforbruk' && <ProsjektforbrukDetail r={data} carry={f.carry} units={units} categories={categories} />}
                    {type === 'vareforbruk' && <VareforbrukDetail r={data} carry={f.carry} units={units} categories={categories} />}
                    {type === 'maskiner' && <MaskinerDetail r={data} carry={f.carry} />}
                    {type === 'hendelser' && <HendelserDetail r={data} onOpenCase={openCase} />}
                    {type === 'avvik' && <AvvikDetail r={data} from={reportFrom} />}
                  </div>
                </CollapsibleContent>
              </Panel>
            </Collapsible>
          )}

          <p className="hidden text-[12px] text-[var(--hr-muted)] print:block">Generert {formatDateTime(data.generatedAt)} · Eksempeldata · Hardanger Rock produksjonsportal</p>
        </>
      )}
      {(type === 'maskiner' || type === 'hendelser') && <CaseSheetHost onChanged={load} />}
    </div>
  );
}
